import type { FormAnswers } from '../types';

export type Tier = 'A' | 'B' | 'C' | 'D';

export interface SenalPuntuada {
  valor: string;
  puntos: number;
}

export interface Perfil {
  tier: Tier;
  score: number;
  señales: {
    // `descarta` = cayó en el umbral mínimo de facturación (ver INGRESOS_DESCARTE).
    ingresos: SenalPuntuada & { descarta: boolean };
    inversion_historial: SenalPuntuada;
    inversion_monto: SenalPuntuada;
    negocio_estructura: SenalPuntuada;
    relacion_javi: SenalPuntuada;
    compromiso: { valor: string; descarta: boolean };
  };
}

// Claves = texto EXACTO de cada opción en data/questions.ts.
const INGRESOS: Record<string, number> = {
  '1K$/menos': 0,
  '1K a 5K': 8,
  '5K a 10K': 15,
  '10K a 20K': 22,
  '20K A 50K': 27,
  '50K a 100K': 30,
  'Más de 100K': 30,
};

const INVERSION_HISTORIAL: Record<string, number> = {
  'Sí, he invertido en varias formaciones, mentorías o agencias y he obtenido resultados sólidos.': 30,
  'Sí, he invertido en algunas, pero los resultados no fueron los que esperaba.': 20,
  'Sí, invertí una vez y no me funcionó como esperaba. No he vuelto a invertir desde entonces.': 10,
  'No, todavía no he invertido en formaciones ni estrategias pagadas. Ha sido todo aprendizaje autodidacta.': 5,
  'Estoy considerando invertir por primera vez y esta podría ser mi primera decisión.': 8,
};

const INVERSION_MONTO: Record<string, number> = {
  'Menos de 500 USD': 2,
  'Entre 500 y 2.000 USD': 6,
  'Entre 2.000 y 5.000 USD': 10,
  'Entre 5.000 y 10.000 USD': 13,
  'Más de 10.000 USD': 15,
  'No he invertido aún': 0,
};

const NEGOCIO_ESTRUCTURA: Record<string, number> = {
  'Realizo eventos presenciales y online de forma activa. Combino masterclasses, retiros, talleres o eventos masivos con mis programas digitales.': 15,
  'Realizo eventos online masivos (masterclasses en vivo, retos, cumbres o lanzamientos digitales) y quiero empezar a integrar el mundo presencial.': 12,
  'Realizo eventos presenciales (talleres, seminarios, retiros o encuentros grupales) y quiero llevar mi negocio también al mundo digital con eventos online.': 11,
  'Todavía no hago eventos. Mi negocio se basa en sesiones individuales o grupos cerrados de trabajo continuo, sin eventos que llenan audiencia.': 6,
  'Estoy comenzando a estructurar mi negocio y aún no he hecho ni eventos presenciales, ni online.': 3,
};

const RELACION_JAVI: Record<string, number> = {
  'Hace 1 semana': 1,
  'Hace 1 mes': 3,
  'Entre 2 y 3 meses': 5,
  'Entre 4 y 6 meses': 7,
  'Entre 7 y 11 meses': 9,
  'Más de 1 año': 10,
};

const COMPROMISO_NO = 'No asistiré y dejaré esperando a mi asesor';

// Gate tier A (decisión de negocio): ingresos ≥ '10K a 20K' (≥22) Y
// inversión con resultados sólidos (30). Requiere AMBOS.
const INGRESOS_GATE_A = 22;
const INVERSION_GATE_A = 30;

// DESCARTE POR ETAPA DEL NEGOCIO (decisión de negocio, 09/10/2026).
// HIT multiplica un negocio que YA vende; no sirve para arrancar uno. Quien
// factura 1.000 USD/mes o menos todavía no está en ese punto, y sentarlo una
// hora con un mentor le deja una lista de cosas que aún no puede ejecutar.
//
// Antes esto NO era un descarte, solo valía 0 puntos — y 0 puntos no bastan
// para caer en D: el resto de la tabla suma hasta 70 (30+15+15+10), así que
// alguien con 1K$/menos llegaba a C (umbral 30, trivial) e incluso a B
// (umbral 55) y se le abría el calendario de PRIORIDAD. Lo único que el
// puntaje ya impedía era el tier A, por el gate de ingresos >=22.
//
// Debe quedar IDÉNTICO en packages/data-layer/src/application-score.js.
const INGRESOS_DESCARTE = '1K$/menos';

function pts(tabla: Record<string, number>, valor: string): number {
  return tabla[valor] ?? 0;
}

/**
 * Calcula el perfil del lead a partir de las respuestas estructuradas del
 * formulario. Función pura y centralizada: ajustar aquí pesos/umbrales sin
 * tocar el formulario ni el webhook. Se envía en el payload a N8N.
 */
export function computePerfil(answers: FormAnswers): Perfil {
  const vIngresos = answers.facturacion_2026 ?? '';
  const vHistorial = answers.inversion_historial ?? '';
  const vMonto = answers.inversion_monto ?? '';
  const vNegocio = answers.negocio_estructura ?? '';
  const vJavi = answers.tiempo_conoce_javi ?? '';
  const vCompromiso = answers.compromiso ?? '';

  const pIngresos = pts(INGRESOS, vIngresos);
  const pHistorial = pts(INVERSION_HISTORIAL, vHistorial);
  const pMonto = pts(INVERSION_MONTO, vMonto);
  const pNegocio = pts(NEGOCIO_ESTRUCTURA, vNegocio);
  const pJavi = pts(RELACION_JAVI, vJavi);

  const score = pIngresos + pHistorial + pMonto + pNegocio + pJavi;
  const descarta = vCompromiso === COMPROMISO_NO;
  // Segundo descarte duro: aún no factura lo mínimo para que HIT le sirva.
  const bajoUmbral = !!INGRESOS_DESCARTE && vIngresos === INGRESOS_DESCARTE;

  let tier: Tier;
  if (descarta || bajoUmbral) {
    tier = 'D';
  } else if (pIngresos >= INGRESOS_GATE_A && pHistorial >= INVERSION_GATE_A) {
    tier = 'A';
  } else if (score >= 55) {
    tier = 'B';
  } else if (score >= 30) {
    tier = 'C';
  } else {
    tier = 'D';
  }

  return {
    tier,
    score,
    señales: {
      ingresos: { valor: vIngresos, puntos: pIngresos, descarta: bajoUmbral },
      inversion_historial: { valor: vHistorial, puntos: pHistorial },
      inversion_monto: { valor: vMonto, puntos: pMonto },
      negocio_estructura: { valor: vNegocio, puntos: pNegocio },
      relacion_javi: { valor: vJavi, puntos: pJavi },
      compromiso: { valor: vCompromiso, descarta },
    },
  };
}
